import { useAuth } from 'react-oidc-context';
import { useMemo } from 'react';

/**
 * Hook para acessar dados do usuário autenticado do Cognito
 * Extrai dados do profile e do id_token decodificado.
 * A busca de atributos adicionais foi removida pois estava causando erros e 
 * a configuração `loadUserInfo: true` no AuthProvider já deve carregar os dados necessários.
 */
export const useUserData = () => {
  const auth = useAuth();

  // Função para decodificar JWT
  const decodeJWT = (token: string) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Erro ao decodificar JWT:', error);
      return null;
    }
  };

  // Decodifica o id_token se disponível
  const idTokenClaims = useMemo(() => {
    if (auth.user?.id_token) {
      return decodeJWT(auth.user.id_token);
    }
    return null;
  }, [auth.user?.id_token]);

  // Extrai dados do usuário de múltiplas fontes
  const userData = useMemo(() => {
    if (!auth.isAuthenticated || !auth.user) {
      return null;
    }

    const profile = auth.user.profile || {};
    const claims = idTokenClaims || {};

    return {
      // ID do usuário
      sub: profile.sub || claims.sub,
      
      // Email
      email: profile.email || claims.email || profile.preferred_username || claims.preferred_username,
      
      // Nome completo - tenta múltiplas fontes
      name: profile.name || 
            claims.name || 
            (profile.given_name && profile.family_name ? `${profile.given_name} ${profile.family_name}` : null) ||
            profile.given_name || 
            claims.given_name,
      givenName: profile.given_name || claims.given_name,
      familyName: profile.family_name || claims.family_name,
      
      // Telefone - tenta múltiplas fontes
      phoneNumber: profile.phone_number || 
                   claims.phone_number || 
                   profile.phone || 
                   claims.phone,
      
      // Outros dados
      username: profile.preferred_username || claims.preferred_username || profile.username,
      
      // Tokens
      accessToken: auth.user.access_token,
      idToken: auth.user.id_token,
      expiresAt: auth.user.expires_at,
      
      // Dados completos para debug
      fullProfile: profile,
      fullIdTokenClaims: claims,
    };
  }, [auth.isAuthenticated, auth.user, idTokenClaims]);

  return {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    user: userData,
    auth, // Acesso completo ao objeto auth se necessário
  };
};